import { useQuery } from '@apollo/client';
import { GET_TICKET_TYPES } from '../../graphql/raffle';
import { useCountdown } from '../Raffle/useCountdown';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ArrowRight } from '../../assets/icons/arrowRight.svg';
import { useEffect } from 'react';
import useMainStore from '../../store/mainState';

const prizeImages: Record<string, string> = {
    CAR: require('../../assets/images/car.png'),
    IPHONE: require('../../assets/images/iphone.png'),
    PHONE: require('../../assets/images/iphone.png'),
    USD: require('../../assets/images/tether-usdt-logo.png'),
    BITCOIN: require('../../assets/images/bitcoin-btc-logo.png'),
};

const periodStyles: Record<string, { accent: string }> = {
    daily: { accent: '#4DBDFF' },
    weekly: { accent: '#4DFF8F' },
    monthly: { accent: '#FF9F4D' },
};

const RaffleItem = ({ ticket, period }: { ticket: any; period: string }) => {
    const { days, hours, minutes, seconds } = useCountdown(ticket.expiresAt);
    const meta = ticket.meta ? (typeof ticket.meta === 'string' ? JSON.parse(ticket.meta) : ticket.meta) : {};
    const prizeImage = meta.prizeType ? prizeImages[meta.prizeType.toUpperCase()] : null;
    const style = periodStyles[period.toLowerCase()] || periodStyles.daily;

    return (
        <div className="flex items-center gap-[14px] py-[10px] border-b border-white/[0.04] last:border-0">
            {prizeImage && (
                <img src={prizeImage} alt="" className="w-[50px] h-[38px] object-contain flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <p className="text-white text-[13px] font-semibold truncate">{ticket.name}</p>
                <p className="text-[11px] font-medium capitalize" style={{ color: style.accent }}>{period}</p>
            </div>
            <div className="flex gap-[4px] flex-shrink-0">
                {[
                    { v: days, l: 'd' },
                    { v: hours, l: 'h' },
                    { v: minutes, l: 'm' },
                    { v: seconds, l: 's' },
                ].map((t) => (
                    <div key={t.l} className="w-[30px] h-[28px] rounded-[6px] bg-white/[0.06] flex items-center justify-center">
                        <span className="text-[11px] font-bold" style={{ color: style.accent }}>{String(t.v).padStart(2, '0')}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const RafflePreview = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { environment, user } = useMainStore();
    const { data, refetch } = useQuery(GET_TICKET_TYPES, {
        variables: { environmentId: environment?.id },
        skip: !environment?.id,
    });

    const allTickets = data?.ticketTypes?.flatMap((group: any) =>
        (group.tickets || []).map((t: any) => ({ ...t, period: group.period }))
    ) || [];

    useEffect(() => {
        (async () => {
            if (user) {
                await refetch()
            }
        })()

    }, [user]);

    if (allTickets.length === 0) return null;

    return (
        <div className="mt-[30px]">
            <div className="flex items-center justify-between mb-[14px]">
                <div className="flex items-center gap-[10px]">
                    <div className="w-[3px] h-[20px] bg-gradient-to-b from-[#FF9F4D] to-[#FF6B4D] rounded-full"></div>
                    <h1 className="text-[15px] lg:text-[20px] font-bold text-white tracking-tight">{t('raffle.title')}</h1>
                    <span className="text-[10px] font-bold text-[#FF9F4D] bg-[#FF9F4D]/10 px-[8px] py-[2px] rounded-[6px] uppercase tracking-wider">{t('raffle.winBig')}</span>
                </div>
                <button
                    onClick={() => navigate('/raffle')}
                    className="flex items-center gap-[6px] text-[12px] lg:text-[14px] font-semibold text-white/40 hover:text-[#4DBDFF] transition-colors duration-200 group"
                >
                    {t('raffle.seeAll')}
                    <ArrowRight className="w-[14px] h-[14px] [&_*]:fill-current transition-transform duration-200 group-hover:translate-x-[3px]" />
                </button>
            </div>
            <div className="rounded-[16px] bg-white/[0.03] border border-white/[0.06] p-[16px]">
                {allTickets.slice(0, 4).map((ticket: any, i: number) => (
                    <RaffleItem key={`${ticket.id}-${i}`} ticket={ticket} period={ticket.period} />
                ))}
            </div>
        </div>
    );
};
